import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Store, Search, MapPin, ShoppingBag, Plus, Minus, Loader2, ArrowLeft, ArrowRight, Phone,
} from "lucide-react";
import { api, fmt, formatError } from "../lib/api";
import VerifiedBadge from "../components/VerifiedBadge";
import useGeo from "../hooks/useGeo";
import LocationPicker from "../components/LocationPicker";

export function VendorsList() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [shopNumber, setShopNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { geo, status: geoStatus, detect, setManual, clear } = useGeo();
  const load = async (g = geo) => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (g) { params.set("lat", g.lat); params.set("lng", g.lng); }
      if (shopNumber) params.set("shop_number", shopNumber);
      const r = await api.get(`/vendors?${params.toString()}`);
      setRows(r.data.rows || []);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  const [headingTop,setHeadingTop]=useState(80);
  useEffect(()=>{const header=document.querySelector('.icaard-topbar');if(!header)return;const update=()=>setHeadingTop(header.getBoundingClientRect().height);update();const observer=new ResizeObserver(update);observer.observe(header);return()=>observer.disconnect();},[]);
  useEffect(() => { load(); }, [geo]);   // eslint-disable-line
  return (
    <div className="space-y-5" data-testid="vendors-list-page">
      <div className="icaard-discovery-heading" style={{top:headingTop}}>
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#F59E0B] flex items-center gap-1.5">
          <Store size={11} /> Food vendors
        </div>
        <h1 className="font-display font-black text-3xl mt-1">Order food & drinks</h1>
        <p className="text-xs text-[#9CA3D4] mt-1">Find a shop by name or its five digit number. Pay with your iCaard balance.</p>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <LocationPicker geo={geo} status={geoStatus} detect={detect} setManual={setManual} clear={clear} accent="#F59E0B" />
          {geo && <span className="text-[9px] text-[#10B981]">📍 nearest first</span>}
        </div>
      </div>
      <div className="surface rounded-2xl p-3 flex flex-col sm:flex-row gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Vendor or category"
               className="flex-1 px-3 py-2 bg-[#0F0726] border border-[#26263A] rounded-xl text-sm text-white"
               data-testid="vendors-search-q" />
        <input value={shopNumber} onChange={(e) => setShopNumber(e.target.value)} placeholder="Shop # (5 digits)" maxLength={5}
               className="sm:w-40 px-3 py-2 bg-[#0F0726] border border-[#26263A] rounded-xl text-sm text-white font-mono"
               data-testid="vendors-search-shop" />
        <button onClick={load} disabled={busy} data-testid="vendors-search-btn"
                className="px-5 py-2 bg-[#F59E0B] text-white font-bold rounded-xl text-sm">{busy ? "Loading…" : "Search"}</button>
      </div>
      {rows.length === 0 ? (
        <div className="surface rounded-2xl p-10 text-center text-[#9CA3D4]"><Store size={28} className="mx-auto mb-2" />No vendors yet</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map(v => (
            <button key={v.id} onClick={() => nav(`/app/vendors/${v.id}`)} data-testid={`vendor-card-${v.id}`}
                    className="surface rounded-2xl overflow-hidden text-left hover:lift transition-all">
              <div className="aspect-[16/10] bg-[#13131E] relative flex items-center justify-center">
                <Store size={24} className="text-[#26263A]" />
                {v.profile_image
                  ? <img src={v.profile_image} alt="" className="absolute inset-0 w-full h-full object-cover"
                         onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  : null}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-1.5">
                  <span className="font-display font-bold truncate">{v.business_name}</span>
                  {v.verified && <VerifiedBadge size={11} />}
                </div>
                <div className="text-[11px] text-[#9CA3D4] mt-0.5">#{v.shop_number} · {v.category}</div>
                <div className="text-[11px] text-[#9CA3D4] flex items-center gap-1 mt-1"><MapPin size={11} />{v.city}{typeof v.distance_km === "number" && <span className="text-[#10B981] font-bold">· {v.approx_location ? "~" : ""}{v.distance_km} km</span>}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function VendorDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [v, setV] = useState(null);
  const [cart, setCart] = useState({});         // menu_item_id → qty
  const [service, setService] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [party, setParty] = useState(2);          // dine-in: number of people
  const [tables, setTables] = useState([]);
  const [tableId, setTableId] = useState("");
  const [busy, setBusy] = useState(false);
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const r = await api.get(`/vendors/${id}`);
        setV(r.data);
        setService(r.data.service_types?.[0] || "take_away");
      } catch (e) { toast.error(formatError(e)); }
      finally { setBusy(false); }
    })();
  }, [id]);

  useEffect(() => {
    if (service !== "take_in" || !v) return;
    api.get(`/vendors/${v.id}/tables?party=${party}`)
      .then((r) => setTables(r.data.rows || []))
      .catch(() => setTables([]));
  }, [service, v, party]);

  const effPrice = (m) => {
    const p = Number(m.price) || 0;
    const d = Number(m.discount_pct);
    if (!d || d <= 0) return p;
    return Math.round(p * (1 - Math.min(90, d) / 100) * 100) / 100;
  };
  const addQ = (mid, delta) => setCart(c => ({ ...c, [mid]: Math.max(0, (c[mid] || 0) + delta) }));
  const total = (v?.menu || []).reduce((s, m) => s + (cart[m.id] || 0) * effPrice(m), 0);

  const deliveryFee = service === "delivery" ? Number(v?.delivery_fee || 0) : 0;
  const grandTotal = total + deliveryFee;

  const placeOrder = async () => {
    const items = Object.entries(cart).filter(([, q]) => q > 0).map(([menu_item_id, qty]) => ({ menu_item_id, qty }));
    if (!items.length) return toast.error("Add at least one item");
    if (service === "delivery" && !deliveryAddress.trim()) return toast.error("Enter a delivery address");
    if (service === "take_in") {
      if (!party || party < 1) return toast.error("How many people are dining?");
      if (!tableId) return toast.error("Select a table with enough free seats");
    }
    setPlacing(true);
    try {
      const r = await api.post("/vendor-orders", {
        vendor_id: v.id, items, service_type: service,
        delivery_address: service === "delivery" ? deliveryAddress.trim() : undefined,
        party_size: service === "take_in" ? party : undefined,
        table_id: service === "take_in" ? tableId : undefined,
      });
      toast.success(`Order placed · GH₵ ${fmt(r.data.order.total)}`);
      setCart({});
      setDeliveryAddress("");
      nav("/app/my-vendor-orders");
    } catch (e) { toast.error(formatError(e)); }
    finally { setPlacing(false); }
  };

  if (busy && !v) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto text-[#9CA3D4]" /></div>;
  if (!v) return null;
  return (
    <div className="space-y-5" data-testid="vendor-detail-page">
      <button onClick={() => nav("/app/vendors")} className="inline-flex items-center gap-1.5 text-[#9CA3D4] text-sm"><ArrowLeft size={14} /> Back</button>
      <div className="surface rounded-2xl overflow-hidden">
        <div className="aspect-[16/6] bg-[#13131E]">{v.profile_image && <img src={v.profile_image} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}</div>
        <div className="p-5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h1 className="font-display font-black text-2xl">{v.business_name}</h1>
            {v.verified && <VerifiedBadge />}
          </div>
          <div className="text-xs text-[#9CA3D4] mt-1 flex items-center gap-2 flex-wrap">
            <span className="font-mono">#{v.shop_number}</span>
            <span>· {v.category}</span>
            <span className="inline-flex items-center gap-1"><MapPin size={11} />{v.city}</span>
            {v.contact_phone && <a href={`tel:${v.contact_phone}`} className="inline-flex items-center gap-1"><Phone size={11} />{v.contact_phone}</a>}
          </div>
        </div>
      </div>

      <div className="surface rounded-2xl p-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3D4] mb-2">Service type</div>
        <div className="flex flex-wrap gap-2">
          {(v.service_types || []).map(st => (
            <button key={st} onClick={() => setService(st)} data-testid={`service-${st}`}
                    className={`px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider font-bold ${service === st ? "bg-[#F59E0B] text-white" : "bg-[#13131E] text-[#9CA3D4]"}`}>
              {st.replace("_", " ")}
            </button>
          ))}
        </div>
        {service === "take_in" && (
          <div className="mt-3 space-y-3" data-testid="dinein-panel">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3D4] mb-1.5">How many people? *</div>
              <div className="flex items-center gap-2">
                <button onClick={() => { setParty(p => Math.max(1, p - 1)); setTableId(""); }} data-testid="party-minus"
                        className="w-8 h-8 rounded-full bg-[#13131E] text-white flex items-center justify-center"><Minus size={13} /></button>
                <span className="font-mono font-black text-lg w-8 text-center" data-testid="party-size">{party}</span>
                <button onClick={() => { setParty(p => Math.min(50, p + 1)); setTableId(""); }} data-testid="party-plus"
                        className="w-8 h-8 rounded-full bg-[#F59E0B] text-white flex items-center justify-center"><Plus size={13} /></button>
                <span className="text-[11px] text-[#9CA3D4]">person{party === 1 ? "" : "s"} dining</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3D4] mb-1.5">Pick a table with enough free seats *</div>
              {tables.length === 0 ? (
                <div className="text-[11px] text-[#9CA3D4]" data-testid="dinein-no-tables">This vendor hasn't set up tables yet. ask staff for a seat.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {tables.map(t => (
                    <button key={t.id} onClick={() => t.fits && setTableId(t.id)} disabled={!t.fits}
                            data-testid={`table-chip-${t.id}`}
                            className={`px-3 py-2 rounded-xl text-left border transition-colors ${
                              tableId === t.id ? "border-[#F59E0B] bg-[#F59E0B]/15"
                              : t.fits ? "border-[#26263A] bg-[#13131E] hover:border-[#F59E0B]/50"
                              : "border-[#26263A] bg-[#13131E] opacity-40 cursor-not-allowed"}`}>
                      <div className="text-xs font-bold">Table {t.table_no}</div>
                      <div className={`text-[10px] ${t.fits ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                        {t.seats_available}/{t.seats} seats free{t.seats_taken > 0 && t.fits ? " · shared" : ""}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {tableId && tables.find(t => t.id === tableId)?.seats_taken > 0 && (
                <div className="text-[10px] text-[#F59E0B] mt-1.5" data-testid="dinein-share-note">
                  You'll join a partially occupied table. {tables.find(t => t.id === tableId).seats_taken} seat(s) already taken.
                </div>
              )}
            </div>
          </div>
        )}
        {service === "delivery" && (
          <div className="mt-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3D4]">Delivery address *</div>
            <textarea value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)}
                      placeholder="Building name, floor / room, landmarks…"
                      rows={2}
                      data-testid="vendor-delivery-address"
                      className="w-full px-3 py-2 bg-[#0F0726] border border-[#26263A] rounded-xl text-sm text-white" />
            {deliveryFee > 0 && (
              <div className="text-[11px] text-[#F59E0B]">Delivery fee: <b className="font-mono">{fmt(deliveryFee)}</b> (added to your total)</div>
            )}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3">Menu</h2>
        {(v.menu || []).length === 0 ? (
          <div className="surface rounded-2xl p-6 text-center text-[#9CA3D4] text-sm">No items on the menu yet</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {v.menu.map(m => (
              <div key={m.id}
                   className={`surface rounded-2xl p-4 flex items-center gap-3 ${m.featured_kind ? "border border-[#F59E0B]/60 ring-2 ring-[#F59E0B]/20" : ""}`}
                   data-testid={`menu-item-${m.id}`}>
                {m.image_url
                  ? <img src={m.image_url} alt="" className="w-16 h-16 rounded-xl object-cover" />
                  : <div className="w-16 h-16 rounded-xl bg-[#13131E] flex items-center justify-center text-[#9CA3D4]"><ShoppingBag size={20} /></div>}
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate flex items-center gap-1.5">
                    {m.name}
                    {m.featured_kind && (
                      <span className="text-[9px] tracking-[0.15em] uppercase font-black bg-[#F59E0B] text-black px-1.5 py-0.5 rounded-md whitespace-nowrap"
                            data-testid={`public-feature-badge-${m.id}`}>
                        Meal of the {m.featured_kind}
                      </span>
                    )}
                    {m.discount_pct > 0 && (
                      <span className="text-[9px] tracking-[0.15em] uppercase font-black bg-[#EF4444] text-white px-1.5 py-0.5 rounded-md whitespace-nowrap"
                            data-testid={`public-discount-badge-${m.id}`}>
                        -{m.discount_pct}%
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#9CA3D4]">
                    {m.item_type} ·{" "}
                    {m.discount_pct > 0 ? (
                      <>
                        <span className="line-through opacity-60">{fmt(m.price)}</span>{" "}
                        <span className="text-[#10B981] font-bold">{fmt(effPrice(m))}</span>
                      </>
                    ) : (
                      fmt(m.price)
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => addQ(m.id, -1)} className="w-7 h-7 rounded-full bg-[#13131E] text-white flex items-center justify-center" data-testid={`menu-minus-${m.id}`}><Minus size={12} /></button>
                  <span className="font-mono font-bold w-6 text-center text-sm">{cart[m.id] || 0}</span>
                  <button onClick={() => addQ(m.id, 1)} className="w-7 h-7 rounded-full bg-[#F59E0B] text-white flex items-center justify-center" data-testid={`menu-plus-${m.id}`}><Plus size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {total > 0 && (
        <div className="surface rounded-2xl p-4 sticky bottom-4 z-10 border border-[#F59E0B]/40 bg-[#F59E0B]/5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3D4]">
                {deliveryFee > 0 ? `Items ${fmt(total)} + delivery ${fmt(deliveryFee)}` : "Cart total"}
              </div>
              <div className="font-mono font-black text-xl" data-testid="vendor-grand-total">{fmt(grandTotal)}</div>
            </div>
            <button onClick={placeOrder} disabled={placing} data-testid="vendor-place-order"
                    className="px-5 py-3 bg-[#F59E0B] hover:bg-[#D97706] text-white font-bold rounded-xl inline-flex items-center gap-2 disabled:opacity-50">
              {placing ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />} Pay from wallet
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default VendorsList;
